import * as PrefabManager from '../PrefabManager';
import * as emitterManager from '../../utils/EmitterManager';
import * as PhysicsParticleEmitter from '../../utils/PhysicsParticleEmitter';
import * as AudioManager from '../../utils/AudioManager';
import * as PrefabBuilder from '../../utils/PrefabBuilder';
import { simulationTime } from '../../utils/SimulationClock';
import { gameRandom } from '../../utils/GameRandom';
import { game } from '../../Game';
import { Settings } from '../../Settings';
import { SoftBreakable } from './SoftBreakable';
import { PAINT_COLORS, RAINBOW_PAINT } from '../../data/paintColors';

const METAL = 0xbfc6cc;
const GHOST_ALPHA = 0.35;
const BURST_RADIUS = 18;
const LID_OFFSET = {x: -0.03, y: -0.51};
const vec = new Box2D.b2Vec2();

// The editor's colour picker adds an alpha byte: '#rrggbbaa'.
const toInt = color => typeof color === 'number' ? color & 0xffffff : parseInt(String(color).replace('#', '').slice(0, 6), 16) || 0;
const toHex = color => `#${toInt(color).toString(16).padStart(6, '0')}`;
const hue = h => {
	const f = n => {
		const k = (n + h * 12) % 12;
		return Math.round(255 * (0.55 - 0.43 * Math.max(-1, Math.min(k - 3, 9 - k, 1))));
	};
	return (f(0) << 16) | (f(8) << 8) | f(4);
};

class PaintBucket extends SoftBreakable {
	constructor(target) {
		super(target);
		this.isPaintBucket = true;
		this.prefabObject.settings = Object.assign({}, PaintBucket.settings, this.prefabObject.settings);
		this.breakingForce = 22;
		this.sound = 'milk-explode';
		this.partsType = ['Plastic', 4];
		this.partsQuantity = 7;
		this.partsSize = 5;
		this.partsForce = 12;
		this.forceBreak = false;
	}

	postConstructor() {
		this.applyColor();
		this.applyVisibility();
	}

	// A hidden bucket hangs where it was placed and touches nothing: only a trigger bursts it.
	init() {
		super.init();
		if (this.prefabObject.settings.isVisible) return;
		this.base.myTexture.visible = false;
		this.base.SetType(Box2D.b2_staticBody);
		for (let fixture = this.base.GetFixtureList(); Box2D.getPointer(fixture) !== Box2D.getPointer(Box2D.NULL); fixture = fixture.GetNext()) fixture.SetSensor(true);
	}

	set(property, value) {
		super.set(property, value);
		if (property === 'color' || property === 'rainbow') this.applyColor();
		if (property === 'isVisible') this.applyVisibility();
	}

	// Ghosted in the editor, so a hidden bucket can still be found and selected.
	applyVisibility() {
		const group = this.lookupObject?.base?.myTexture;
		if (group) group.alpha = this.prefabObject.settings.isVisible ? 1 : GHOST_ALPHA;
	}

	setPaintColor(name) {
		if (name === 'Rainbow') {
			this.set('rainbow', true);
			return;
		}
		this.set('rainbow', false);
		this.set('color', PAINT_COLORS[name] || PaintBucket.settings.color);
	}

	colorLayer() {
		const group = this.lookupObject?.base?.myTexture;
		const index = group?.data?.graphicObjects?.findIndex(graphicObject => graphicObject[5] === 'color') ?? -1;
		return index >= 0 ? group.children[index] : null;
	}

	applyColor() {
		const layer = this.colorLayer();
		if (!layer) return;
		const {color, rainbow} = this.prefabObject.settings;
		layer.tint = rainbow ? hue(simulationTime() / 4000) : toInt(color);
	}

	paintColors() {
		if (this.prefabObject.settings.rainbow) return RAINBOW_PAINT;
		const color = toInt(this.prefabObject.settings.color);
		const darker = ((((color >> 16) & 255) * 0.82) << 16) | ((((color >> 8) & 255) * 0.82) << 8) | ((color & 255) * 0.82);
		return [toHex(color), toHex(color), toHex(darker)];
	}

	queueBreak() {
		this.forceBreak = true;
	}

	update() {
		if (this.forceBreak) {
			this.forceBreak = false;
			this.break();
			return;
		}
		if (this.prefabObject.settings.rainbow) this.applyColor();
		super.update();
	}

	break() {
		if (this.destroyed || this.prefabObject.destroyed) return;
		const base = this.base;
		const angle = base.GetAngle();
		const position = {x: base.GetPosition().x, y: base.GetPosition().y};
		const velocity = {x: base.GetLinearVelocity().x, y: base.GetLinearVelocity().y};
		vec.Set(LID_OFFSET.x, LID_OFFSET.y);
		const lid = base.GetWorldPoint(vec);
		const lidPosition = {x: lid.x, y: lid.y};
		const gravity = game.world.GetGravity();
		const gravityLength = Math.sqrt(gravity.x * gravity.x + gravity.y * gravity.y);
		const up = gravityLength > 0 ? {x: -gravity.x / gravityLength, y: -gravity.y / gravityLength} : {x: 0, y: -1};
		const colors = this.paintColors();
		const ptm = Settings.PTM;

		const settings = this.prefabObject.settings;
		for (let i = Math.max(1, Math.round(settings.paintAmount)); i > 0; i--) {
			const burst = emitterManager.playOnceEmitter('paintBurst', null, position, 0, colors);
			if (burst) burst.spawnCircle = {x: 0, y: 0, r: BURST_RADIUS * (0.7 + Math.random() * 0.6)};
			burst?.setSpawnVelocity(velocity.x * ptm, velocity.y * ptm);
			const splash = emitterManager.playOnceEmitter('paintSplash', null, lidPosition, Math.atan2(up.y, up.x) * game.editor.RAD2DEG, colors);
			splash?.setSpawnVelocity(velocity.x * ptm, velocity.y * ptm);
		}

		AudioManager.playSFX(this.sound, 0.3, 0.9 + 0.2 * Math.random(), position);
		if (settings.isVisible) AudioManager.playSFX(`metal-impact${1 + Math.floor(Math.random() * 4)}`, 0.25, 0.9 + 0.2 * Math.random(), position);

		this.destroy();

		// A hidden bucket only throws paint.
		if (!settings.hasDebris || !settings.isVisible) return;
		const parts = Array.from({length: this.partsType[1]}, (_, i) => `${this.partsType[0]}_${i + 1}`);
		PhysicsParticleEmitter.emitDebris(parts, position, this.partsQuantity, this.partsSize, this.partsForce, true, [METAL, METAL, toInt(colors[0])], angle, this.partsOffset);

		vec.Set(lidPosition.x, lidPosition.y);
		const {lookupObject} = PrefabBuilder.generatePrefab(vec, angle * game.editor.RAD2DEG, 'PaintBucketLid', true);
		const lidBody = lookupObject._bodies[0];
		const side = gameRandom() * 2 - 1;
		const push = 7 + gameRandom() * 4;
		vec.Set(velocity.x + (up.x + up.y * side * 0.5) * push, velocity.y + (up.y - up.x * side * 0.5) * push);
		lidBody.SetLinearVelocity(vec);
		lidBody.SetAngularVelocity((gameRandom() * 2 - 1) * 9);
	}
}

PaintBucket.settings = Object.assign({}, SoftBreakable.settings, {
	color: PAINT_COLORS.Red,
	rainbow: false,
	isVisible: true,
	paintAmount: 1,
});

PaintBucket.settingsOptions = Object.assign({}, SoftBreakable.settingsOptions, {
	color: '$color',
	rainbow: true,
	isVisible: true,
	paintAmount: {min: 1, max: 10, step: 1},
});

const texture = (refName, ID, textureName) => [1, 0, 0, 0, 'paintBucket', refName, ID, textureName, null, null, null, null, false, '#FFFFFF', 1, 1, 1, 0, 0, 0, true];

PrefabManager.prefabLibrary.PaintBucket = {
	json: JSON.stringify({objects: [
		[0, 0, 0, 0, 'paintBucket', 'base', 0, ['#999999'], ['#000'], [0], false, true, [[{x: 0.5333, y: -0.6583}, {x: 0.5333, y: 0.6583}, {x: -0.5333, y: 0.6583}, {x: -0.5333, y: -0.6583}]], [1.5], [0], [0], '', [0], true, false, false, [0.5], [0.2], false, true, false, false],
		[7, 0, -4.75, 0, 'paintBucket', 'art', 1, [
			texture('bucket', 2, 'Paint_bucket instance 10000'),
			texture('color', 3, 'Paint_color instance 10000'),
			texture('lid', 4, 'Paint_lid instance 10000'),
		], 0, 4.75, Math.PI / 2, 0, 1],
	]}),
	class: PaintBucket,
	library: PrefabManager.LIBRARY_MISC,
};

PrefabManager.prefabLibrary.PaintBucketLid = {
	json: JSON.stringify({objects: [
		[0, 0, 0, 0, 'paintBucketLid', 'body', 0, ['#999999'], ['#000'], [0], false, true, [[{x: 0.58, y: -0.17}, {x: 0.58, y: 0.17}, {x: -0.58, y: 0.17}, {x: -0.58, y: -0.17}]], [1], [0], [0], '', [0], true, false, false, [0.5], [0.2], false, true, false, false],
		[1, 1, 10.5, 0, 'paintBucketLid', 'texture', 1, 'Paint_lid instance 10000', 0, Math.hypot(1, 10.5), Math.atan2(-10.5, 1), 0, false, '#FFFFFF', 1, 1, 1, 0, 0, 0, true],
	]}),
	class: PrefabManager.basePrefab,
};
